import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Clock, LogOut, ShoppingBag } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const links = [
  { to: "/shop", label: "Shop", id: "nav-shop-link" },
  { to: "/orders", label: "My Orders", id: "nav-my-orders-link" },
  { to: "/request", label: "Request a Peptide", id: "nav-request-link" },
  { to: "/calculator", label: "Reconstitution Calculator", id: "nav-dosing-link" },
  { to: "/refer", label: "Refer a Friend", id: "nav-refer-link" },
];

const PendingNotice = ({ name }) => (
  <div className="mx-auto max-w-xl px-6 py-24 text-center rise" data-testid="pending-approval-notice">
    <div className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full border border-amber-500/40 bg-amber-500/10">
      <Clock className="h-6 w-6 text-amber-400" />
    </div>
    <h1 className="text-3xl font-semibold sm:text-4xl">Thanks, {name}. You're on the list.</h1>
    <p className="mt-4 text-muted-foreground">
      Your account is waiting for approval from Korevex Bio. Once approved, you'll be able to browse the catalog and
      place orders. Check back soon.
    </p>
  </div>
);

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const approved = user.status === "approved";

  return (
    <div className="min-h-screen">
      <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-center text-xs text-amber-200" data-testid="research-use-notice">
        All products are for research use only. Not for human consumption.
      </div>
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
          <Logo to="/shop" />
          {approved && (
            <nav className="hidden items-center gap-1 md:flex">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.id}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-1.5 text-sm transition-colors ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-2">
            {approved && (
              <Button variant="outline" size="sm" onClick={() => nav("/cart")} data-testid="nav-cart-button" className="gap-2">
                <ShoppingBag className="h-4 w-4" />
                <span className="font-mono-data" data-testid="nav-cart-count">{count}</span>
              </Button>
            )}
            <span className="hidden text-sm text-muted-foreground lg:inline" data-testid="nav-user-name">{user.name}</span>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {approved && (
          <nav className="flex gap-1 overflow-x-auto border-t border-border/60 px-4 py-2 md:hidden">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`${l.id}-mobile`}
                className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1 text-sm ${isActive ? "bg-secondary" : "text-muted-foreground"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
      <main>{approved ? <Outlet /> : <PendingNotice name={user.name} />}</main>
    </div>
  );
}
